import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import './Header.css'; // Your existing Header.css
import '@fortawesome/fontawesome-free/css/all.min.css'; // FontAwesome
import logo from '../assets/img/logo.png';
import brochurePDF from '../assets/img/ABP Big Bag Brochure.pdf';

const Header = () => {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Main Products Dropdown (for mobile and potentially click-driven desktop)
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);

  // Sub-category Dropdowns (primarily for mobile or click-driven desktop)
  const [isBigBagsSubOpen, setIsBigBagsSubOpen] = useState(false);
  const [isBagsSubOpen, setIsBagsSubOpen] = useState(false);
  const [isSpecialProductsSubOpen, setIsSpecialProductsSubOpen] = useState(false);

  // Function to scroll to the top of the page
  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const toggleMobileNav = () => {
    setIsMobileNavOpen(!isMobileNavOpen);
    // Close all dropdowns when mobile nav is toggled
    if (isMobileNavOpen) { // If closing
      closeAllDropdowns();
    }
  };

  const closeAllDropdowns = () => {
    setIsProductsDropdownOpen(false);
    setIsBigBagsSubOpen(false);
    setIsBagsSubOpen(false);
    setIsSpecialProductsSubOpen(false);
  };

  // Toggle for the main "Products" dropdown
  const toggleMainProductsDropdown = (e) => {
    e.preventDefault(); // Prevent navigation if the main link is just a toggle
    const currentlyOpening = !isProductsDropdownOpen;
    setIsProductsDropdownOpen(currentlyOpening);
    // If main products dropdown is closing, close all its children
    if (!currentlyOpening) {
      setIsBigBagsSubOpen(false);
      setIsBagsSubOpen(false);
      setIsSpecialProductsSubOpen(false);
    }
  };

  // Toggles for sub-category dropdowns
  const toggleBigBagsSub = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBigBagsSubOpen(!isBigBagsSubOpen);
    setIsBagsSubOpen(false); // Close other sibling sub-dropdowns
    setIsSpecialProductsSubOpen(false);
  };

  const toggleBagsSub = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBagsSubOpen(!isBagsSubOpen);
    setIsBigBagsSubOpen(false);
    setIsSpecialProductsSubOpen(false);
  };

  const toggleSpecialProductsSub = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSpecialProductsSubOpen(!isSpecialProductsSubOpen);
    setIsBigBagsSubOpen(false);
    setIsBagsSubOpen(false);
  };

  // Effect to add/remove class from body for mobile navigation
  useEffect(() => {
    const body = document.querySelector('body');
    if (body) {
      if (isMobileNavOpen) {
        body.classList.add('mobile-nav-active');
      } else {
        body.classList.remove('mobile-nav-active');
      }
    }
  }, [isMobileNavOpen]);

  // Closes mobile nav, dropdowns, and scrolls to top when a mobile link is clicked
  const handleMobileLinkClick = () => {
    scrollToTop(); // Scroll to top
    if (isMobileNavOpen) {
      setIsMobileNavOpen(false);
    }
    closeAllDropdowns(); // Close all dropdowns on any link click from mobile
  };

  // Effect to handle clicks outside the mobile navigation
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileNavOpen &&
        !event.target.closest('.mobile-nav-toggle') &&
        !event.target.closest('.mobile-nav')) {
        setIsMobileNavOpen(false);
        closeAllDropdowns(); // Also close dropdowns
      }
    };
    if (isMobileNavOpen) {
      document.addEventListener('click', handleClickOutside, true);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isMobileNavOpen]);

  // Favicon (can remain as is)
  useEffect(() => {
    const faviconPath = '/assets/img/favicon.png';
    let favicon = document.getElementById('favicon');
    if (favicon) {
      favicon.href = faviconPath;
    } else {
      const newFavicon = document.createElement('link');
      newFavicon.id = 'favicon';
      newFavicon.rel = 'icon';
      newFavicon.href = faviconPath;
      document.head.appendChild(newFavicon);
    }
  }, []);

  // Updated Product link structure
  const productLinks = {
    bigBagsParent: "/products/big-bags",
    bigBags: [
      { name: "All Big Bags (FIBC)", path: "/products/big-bags" },
      { name: "Standard Big Bags", path: "/products/big-bags-standard" },
      { name: "1 & 2 Loop Big Bags", path: "/products/big-bags-1-2-loops" },
      { name: "4 Loop Big Bags", path: "/products/big-bags-4-loops" },
      { name: "Ventilated Big Bags", path: "/products/big-bags-ventilated" },
      { name: "Q-Bags (Baffle Bags)", path: "/products/big-bags-q-bag" },
    ],
    bagsParent: "/products/bags",
    bags: [
      { name: "All PP & Kraft Bags", path: "/products/bags" },
      { name: "Standard PP Bags", path: "/products/bags-standard-pp" },
      { name: "Kraft Paper Bags", path: "/products/bags-kraft-paper" },
      { name: "Laminated PP Bags", path: "/products/bags-laminated" },
      { name: "BOPP Woven Bags", path: "/products/bags-bopp" },
      { name: "Block Bottom Bags", path: "/products/bags-block-bottom" }
    ],
    specialProductsParent: "/products/special-products",
    specialProducts: [
      { name: "All Special Products", path: "/products/special-products" },
      { name: "Specialized PE Films", path: "/products/special-products-pe-film" },
      { name: "Thermal Pallet Cover", path: "/products/special-products-thermal-pallet-cover" },
      { name: "Container Safety Sheets", path: "/products/special-products-container-safety-sheets" },
    ]
  };

  return (
    <>
      <section id="topbar" className="d-flex align-items-center">
        <div className="container d-flex justify-content-between">
          <div className="contact-info d-flex align-items-center">
            <a href="mailto:info@abpbigbag.com">
              <i className="fas fa-envelope"></i> info@abpbigbag.com
            </a>
            <a href="tel:+36706576199">
              <i className="fas fa-phone"></i> +36 70 657 61-99
            </a>
            <a href="tel:+36703693425">
              <i className="fas fa-phone"></i> +36 70 369 34-25
            </a>
          </div>
          <div className="brochure-link">
            <a href={brochurePDF} target="_blank" rel="noopener noreferrer">
              <i className="fas fa-file-pdf"></i> Download Brochure
            </a>
          </div>
        </div>
      </section>

      <header id="header">
        <div className="container d-flex align-items-center justify-content-between">
          <div className="logo">
            <Link to="/" onClick={scrollToTop}>
              <img src={logo} alt="ABP Big Bag" className="img-fluid" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="nav-menu d-none d-lg-block">
            <ul>
              <li><NavLink to="/" end className={({ isActive }) => isActive ? "active" : ""} onClick={scrollToTop}>Home</NavLink></li>
              <li><NavLink to="/about" className={({ isActive }) => isActive ? "active" : ""} onClick={scrollToTop}>About</NavLink></li>

              {/* Main Products Dropdown - Desktop */}
              <li className="drop-down">
                <Link to="/products" onClick={scrollToTop}>Products <i className="fas fa-chevron-down"></i></Link>
                <ul>
                  {/* Big Bags (FIBC) Sub-Dropdown */}
                  <li className="drop-down">
                    <Link to={productLinks.bigBagsParent} onClick={scrollToTop}>Big Bags (FIBC) <i className="fas fa-chevron-right"></i></Link>
                    <ul>
                      {productLinks.bigBags.map(link => (
                        <li key={link.name}><Link to={link.path} onClick={scrollToTop}>{link.name}</Link></li>
                      ))}
                    </ul>
                  </li>

                  {/* PP & Kraft Bags Sub-Dropdown */}
                  <li className="drop-down">
                    <Link to={productLinks.bagsParent} onClick={scrollToTop}>PP & Kraft Bags <i className="fas fa-chevron-right"></i></Link>
                    <ul>
                      {productLinks.bags.map(link => (
                        <li key={link.name}><Link to={link.path} onClick={scrollToTop}>{link.name}</Link></li>
                      ))}
                    </ul>
                  </li>

                  {/* Special Products Sub-Dropdown */}
                  <li className="drop-down">
                    <Link to={productLinks.specialProductsParent} onClick={scrollToTop}>Special Products <i className="fas fa-chevron-right"></i></Link>
                    <ul>
                      {productLinks.specialProducts.map(link => (
                        <li key={link.name}><Link to={link.path} onClick={scrollToTop}>{link.name}</Link></li>
                      ))}
                    </ul>
                  </li>
                </ul>
              </li>

              <li><NavLink to="/sectors" className={({ isActive }) => isActive ? "active" : ""} onClick={scrollToTop}>Sectors</NavLink></li>
              <li><NavLink to="/sales" className={({ isActive }) => isActive ? "active" : ""} onClick={scrollToTop}>Sales Network</NavLink></li>
              <li><NavLink to="/contact" className={({ isActive }) => isActive ? "active" : ""} onClick={scrollToTop}>Contact</NavLink></li>
            </ul>
          </nav>

          <button type="button" className="mobile-nav-toggle d-lg-none" onClick={toggleMobileNav} aria-label="Toggle navigation">
            <i className={isMobileNavOpen ? "fas fa-times" : "fas fa-bars"}></i>
          </button>
        </div>
      </header>

      {/* Mobile Navigation Overlay and Menu */}
      {isMobileNavOpen && <div className="mobile-nav-overly" onClick={toggleMobileNav}></div>}
      <nav className={`mobile-nav d-lg-none ${isMobileNavOpen ? 'mobile-nav-active' : ''}`}>
        <ul>
          <li><NavLink to="/" end className={({ isActive }) => isActive ? "active" : ""} onClick={handleMobileLinkClick}>Home</NavLink></li>
          <li><NavLink to="/about" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMobileLinkClick}>About</NavLink></li>

          {/* Main Products Dropdown - Mobile */}
          <li className={`drop-down ${isProductsDropdownOpen ? 'active' : ''}`}>
            <a href="#" onClick={toggleMainProductsDropdown}>
              Products <i className={isProductsDropdownOpen ? "fas fa-chevron-up" : "fas fa-chevron-down"}></i>
            </a>
            <ul style={{ display: isProductsDropdownOpen ? 'block' : 'none' }}>

              {/* Big Bags (FIBC) Sub-Dropdown - Mobile */}
              <li className={`drop-down ${isBigBagsSubOpen ? 'active' : ''}`}>
                <a href="#" onClick={toggleBigBagsSub}>
                  Big Bags (FIBC) <i className={isBigBagsSubOpen ? "fas fa-chevron-up" : "fas fa-chevron-down"}></i>
                </a>
                <ul style={{ display: isBigBagsSubOpen ? 'block' : 'none' }}>
                  {productLinks.bigBags.map(link => (
                    <li key={link.name}><Link to={link.path} onClick={handleMobileLinkClick}>{link.name}</Link></li>
                  ))}
                </ul>
              </li>

              {/* PP & Kraft Bags Sub-Dropdown - Mobile */}
              <li className={`drop-down ${isBagsSubOpen ? 'active' : ''}`}>
                <a href="#" onClick={toggleBagsSub}>
                  PP & Kraft Bags <i className={isBagsSubOpen ? "fas fa-chevron-up" : "fas fa-chevron-down"}></i>
                </a>
                <ul style={{ display: isBagsSubOpen ? 'block' : 'none' }}>
                  {productLinks.bags.map(link => (
                    <li key={link.name}><Link to={link.path} onClick={handleMobileLinkClick}>{link.name}</Link></li>
                  ))}
                </ul>
              </li>

              {/* Special Products Sub-Dropdown - Mobile */}
              <li className={`drop-down ${isSpecialProductsSubOpen ? 'active' : ''}`}>
                <a href="#" onClick={toggleSpecialProductsSub}>
                  Special Products <i className={isSpecialProductsSubOpen ? "fas fa-chevron-up" : "fas fa-chevron-down"}></i>
                </a>
                <ul style={{ display: isSpecialProductsSubOpen ? 'block' : 'none' }}>
                  {productLinks.specialProducts.map(link => (
                    <li key={link.name}><Link to={link.path} onClick={handleMobileLinkClick}>{link.name}</Link></li>
                  ))}
                </ul>
              </li>
            </ul>
          </li>

          <li><NavLink to="/sectors" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMobileLinkClick}>Sectors</NavLink></li>
          <li><NavLink to="/sales" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMobileLinkClick}>Sales Network</NavLink></li>
          <li><NavLink to="/contact" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMobileLinkClick}>Contact</NavLink></li>
          
          {/* Brochure link for mobile */}
          <li>
            <a href="/assets/img/ABP Big Bag Brochure.pdf" target="_blank" rel="noopener noreferrer" onClick={handleMobileLinkClick}>
              <i className="fas fa-file-pdf"></i> Download Brochure
            </a>
          </li>
        </ul>
      </nav>
    </>
  );
};

export default Header;